import {IconProp} from '@fortawesome/fontawesome-svg-core';
import {useQuery} from 'react-query';
import {CatalogItemVO, LayerVO, SearchFilterApi, SearchFilterLayerTargetVO, SearchFilterNodeVO} from '../../../api';
import {CiselnikFilterNode} from './filterTreeTypes';
import {layerColorToCss} from '../searchPanelTypes';

/**
 * Namapuje uzly vrácené serverem ({@link SearchFilterNodeVO}) na klientský {@link CiselnikFilterNode}.
 * Tvary jsou skoro shodné; liší se jen `icon` (server: název glyphu jako string → klient: IconProp).
 */
export const mapSearchFilterNodes = (nodes: SearchFilterNodeVO[]): CiselnikFilterNode[] =>
    nodes.map(n => ({
        id: n.id,
        parentId: n.parentId ?? null,
        label: n.label,
        color: n.color,
        labelColor: n.labelColor,
        // TODO(server): až server bude posílat názvy glyphů, převést je na IconProp (mapa názvů → ikona).
        icon: n.icon as IconProp | undefined,
        children: n.children ? mapSearchFilterNodes(n.children) : undefined,
        target: n.target,
    }));

/**
 * Doplní LAYER uzlům (koncové vrstvy diskriminátoru) URL marker obrázku vrstvy z konfigurace vrstev,
 * aby se u nich ve filtru zobrazila ikona vrstvy. Ostatní uzly nechává beze změny.
 */
export const attachLayerIcons = (nodes: CiselnikFilterNode[], layersConfig?: {[key: string]: LayerVO}, serverContextPath?: string): CiselnikFilterNode[] =>
    nodes.map(node => {
        const layerId = node.target?.kind === 'LAYER' ? (node.target as SearchFilterLayerTargetVO).layerId : undefined;
        const layerConfig = layerId != null ? layersConfig?.[String(layerId)] : undefined;
        const imgName = layerConfig?.layerImage ?? layerConfig?.style?.marker;
        // Barva vrstvy shodně se zbytkem aplikace (LegendModal): style.color, jinak iconColor.
        const layerColor = layerColorToCss(layerConfig?.style?.color ?? layerConfig?.iconColor);
        return {
            ...node,
            iconUrl: imgName ? `${serverContextPath ?? ''}/api/files/img/${imgName}` : node.iconUrl,
            labelColor: layerColor ?? node.labelColor,
            children: node.children ? attachLayerIcons(node.children, layersConfig, serverContextPath) : node.children,
        };
    });

/**
 * Sestaví dimenzi VRSTEV filtru přímo z katalogu (klient má všechny vrstvy tématu, i nezapnuté) – filtr
 * je tak rozšíření/nahrazení katalogu. Katalog se ZPLOŠŤUJE na samotné KONCOVÉ vrstvy: rodičovské
 * kategorie (diskriminátorový rodič) se do filtru nepromítají, protože samy o sobě nejsou vrstvou –
 * projdou se jen kvůli sestupu na jejich koncové vrstvy. Zachovává pořadí katalogu (pre-order).
 * Každá koncová vrstva (`layerId`) → list s LAYER targetem (přepínač viditelnosti vrstvy).
 * Uzly mají ZÁPORNÁ id, aby nekolidovala s kladnými id atributových uzlů ze serveru.
 * Ikonu a barvu vrstvy doplní {@link attachLayerIcons}.
 */
export const buildCatalogFilterNodes = (catalog?: CatalogItemVO[]): CiselnikFilterNode[] => {
    if (!catalog || catalog.length === 0) {
        return [];
    }
    const seq = {value: 0};
    const walk = (items: CatalogItemVO[]): CiselnikFilterNode[] =>
        items.flatMap(item => {
            // Kategorie (rodič s potomky) → do filtru se nepromítá, sestoupíme rovnou na její koncové vrstvy.
            if (item.children && item.children.length > 0) {
                return walk(item.children);
            }
            if (item.layerId == null) {
                return [];
            }
            const id = --seq.value; // -1, -2, … (oddělené od kladných serverových id)
            return [
                {
                    id,
                    parentId: null,
                    label: item.label ?? '',
                    target: {kind: 'LAYER', layerId: item.layerId} as SearchFilterLayerTargetVO,
                },
            ];
        });
    return walk(catalog);
};

/**
 * Zdroj dat stromového filtru pro SearchPanel.
 *
 * Strom se skládá z číselníků AKTIVNÍCH vrstev (`activeLayers`), jejichž koncové uzly jsou aktivní
 * vrstvy – proto se sem aktivní vrstvy předávají a server je dostane jako `layerIds`.
 * Server je {@code SearchFilterController.getSearchFilterTree}.
 */
export const useSearchFilterTree = (topicId?: string | number, activeLayers: Array<string | number> = [], context?: string): CiselnikFilterNode[] => {
    const layerIds = activeLayers.map(Number).filter(n => !Number.isNaN(n));
    const {data} = useQuery(
        [
            'search-filter-tree',
            topicId,
            layerIds
                .slice()
                .sort((a, b) => a - b)
                .join(','),
            context,
        ],
        async () => mapSearchFilterNodes((await SearchFilterApi.getSearchFilterTree(String(topicId), layerIds, context)).data.nodes ?? []),
        {enabled: !!topicId && layerIds.length > 0, staleTime: Infinity},
    );
    return data ?? [];
};
